import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import CommentPost from 'flarum/forum/components/CommentPost';

/**
 * Members-only downloads (src/DownloadController.php, /downloads/<path>):
 * - a guest who follows a download link is asked to log in. The file is
 *   remembered for this tab, and once logging in has reloaded the page the
 *   download starts by itself. A guest who opens a download address
 *   directly is sent here with ?bdDownload=<path>, and asked the same;
 * - download links in posts look like buttons (.BdDownloadLink, in
 *   post-content.less).
 *
 * The pending download is kept in sessionStorage rather than the address:
 * a discussion page rewrites its address as you scroll (…/d/1-slug/3), which
 * would drop a parameter before logging in reloads the page.
 */

const PARAM = 'bdDownload';
const KEY = 'bdPendingDownload';
// A download asked for longer ago than this is no longer wanted.
const PENDING_FOR = 10 * 60 * 1000;
function prefix() {
  return `${new URL(app.forum.attribute('baseUrl')).pathname.replace(/\/$/, '')}/downloads/`;
}

/** The download path a link points to, or null if it isn't a download. */
function downloadPath(link) {
  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin || !url.pathname.startsWith(prefix())) return null;
  return decodeURIComponent(url.pathname.slice(prefix().length));
}
function downloadUrl(path) {
  return `${app.forum.attribute('baseUrl')}/downloads/${path.split('/').map(encodeURIComponent).join('/')}`;
}

/** Remembers (or, with null, forgets) the download to start after logging in. */
function remember(path) {
  try {
    if (path) {
      window.sessionStorage.setItem(KEY, JSON.stringify({
        path,
        at: Date.now()
      }));
    } else {
      window.sessionStorage.removeItem(KEY);
    }
  } catch {
    // Storage blocked: the download just won't start by itself.
  }
}
function pending() {
  try {
    const {
      path,
      at
    } = JSON.parse(window.sessionStorage.getItem(KEY) || 'null') || {};
    return typeof path === 'string' && Date.now() - at < PENDING_FOR ? path : null;
  } catch {
    return null;
  }
}
function askToLogIn(path) {
  remember(path);
  app.alerts.show({
    type: 'info'
  }, app.translator.trans('bimmerdock-theme.forum.downloads.log_in', {
    file: path.split('/').pop()
  }));
  app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
}
export default function downloads() {
  app.beforeMount(() => {
    // Sent here by the server from a download address: take the parameter
    // out of the address before the page mounts and rewrites it.
    const params = new URLSearchParams(window.location.search);
    const sent = params.get(PARAM);
    if (sent) {
      params.delete(PARAM);
      const query = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
    }

    // Once mounted, when the modal manager exists.
    window.setTimeout(() => {
      if (!app.session.user) {
        if (sent) askToLogIn(sent);
        return;
      }
      const path = sent || pending();
      remember(null);
      if (path) window.location.href = downloadUrl(path);
    });
  });

  // Before the forum's own link handling (capture), so a download is never
  // routed inside the page.
  document.addEventListener('click', e => {
    const link = e.target?.closest?.('a[href]');
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const path = downloadPath(link);
    if (path === null) return;
    e.preventDefault();
    e.stopPropagation();
    if (app.session.user) {
      window.location.href = link.href;
    } else {
      askToLogIn(path);
    }
  }, true);
  extend(CommentPost.prototype, ['oncreate', 'onupdate'], function () {
    this.element.querySelectorAll('.Post-body a[href]').forEach(link => {
      link.classList.toggle('BdDownloadLink', downloadPath(link) !== null);
    });
  });
}